import { useState, useRef, useEffect, useCallback } from 'react';
import { extractBackendErrorCode } from '@/core/api/http-errors';
import { aiService } from '../api/ai.service';

export type VoiceDictationState = 'idle' | 'recording' | 'processing' | 'error';

export type VoiceDictationError =
  | 'permission-denied'
  | 'no-microphone'
  | 'api-error'
  | 'too-short'
  | 'not-recognized';

/**
 * Locale key per failure. A map rather than a key interpolated from the error value: the
 * union is kebab-case and the locale keys are camelCase like every other key in the
 * project, so `errors.${error}` resolved to nothing and every dictation failure showed
 * the raw key as its message.
 */
export const VOICE_ERROR_KEY: Record<VoiceDictationError, string> = {
  'permission-denied': 'quickCreate.voiceDictation.errors.permissionDenied',
  'no-microphone': 'quickCreate.voiceDictation.errors.noMicrophone',
  'api-error': 'quickCreate.voiceDictation.errors.apiError',
  'too-short': 'quickCreate.voiceDictation.errors.tooShort',
  'not-recognized': 'quickCreate.voiceDictation.errors.notRecognized',
};

export interface UseVoiceDictationOptions {
  onTranscription: (text: string) => void;
  onError: (error: VoiceDictationError) => void;
  language?: string;
}

export interface UseVoiceDictationReturn {
  state: VoiceDictationState;
  startRecording: () => void;
  stopRecording: () => void;
  duration: number;
  isSupported: boolean;
  /** True while audio is being sent to the transcription API. */
  isProcessing: boolean;
  /**
   * Invalidates any pending transcription so its result is discarded.
   * Call this when the user manually edits the target textarea while a
   * transcription is still in flight.
   */
  notifyManualEdit: () => void;
}

const SILENCE_THRESHOLD = 0.01;
const SILENCE_TIMEOUT_MS = 3000;
const MIN_DURATION_MS = 500;

/**
 * Hook for voice dictation using MediaRecorder API.
 * Records audio, sends to speech-to-text API, and returns transcription.
 *
 * Features:
 * - Auto-stops after 3s of silence
 * - Minimum duration check (0.5s) to avoid accidental taps
 * - Cleanup on unmount
 * - Browser support detection
 */
export function useVoiceDictation({
  onTranscription,
  onError,
  language,
}: UseVoiceDictationOptions): UseVoiceDictationReturn {
  const [state, setState] = useState<VoiceDictationState>('idle');
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const transcriptionGenerationRef = useRef(0);
  const pendingGenerationRef = useRef<number | null>(null);

  const isSupported =
    typeof MediaRecorder !== 'undefined' && typeof navigator.mediaDevices !== 'undefined';

  const notifyManualEdit = useCallback(() => {
    transcriptionGenerationRef.current += 1;
  }, []);

  function cleanup() {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    mediaRecorderRef.current = null;
    analyserRef.current = null;
    chunksRef.current = [];
  }

  function detectSilence() {
    if (!analyserRef.current) return;

    const dataArray = new Float32Array(analyserRef.current.fftSize);
    analyserRef.current.getFloatTimeDomainData(dataArray);

    const maxAmplitude = Math.max(...dataArray.map(Math.abs));
    const isSilent = maxAmplitude < SILENCE_THRESHOLD;

    if (isSilent) {
      if (!silenceTimeoutRef.current) {
        silenceTimeoutRef.current = setTimeout(() => {
          if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
        }, SILENCE_TIMEOUT_MS);
      }
    } else {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
    }
  }

  async function startRecording() {
    if (!isSupported) {
      onError('no-microphone');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up audio analysis for silence detection
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const endTime = Date.now();
        const recordingDuration = endTime - startTimeRef.current;

        // Check minimum duration
        if (recordingDuration < MIN_DURATION_MS) {
          cleanup();
          setState('idle');
          onError('too-short');
          return;
        }

        const requestGeneration = pendingGenerationRef.current;
        setState('processing');

        try {
          const audioBlob = new Blob(chunksRef.current, { type: mimeType });
          const result = await aiService.transcribeAudio(audioBlob, language);

          // If the user manually edited the textarea after starting this recording,
          // discard the transcription so it cannot overwrite their typed input.
          if (requestGeneration === transcriptionGenerationRef.current) {
            onTranscription(result.transcription);
          }

          // Outside the guard on purpose: the button is disabled while processing, so
          // returning to idle only on the delivered path left the microphone dead for
          // good in exactly the case the guard exists to serve — the user typed while
          // the audio was being transcribed.
          setState('idle');
        } catch (error: unknown) {
          const errorCode = extractBackendErrorCode(error);
          const isNotRecognized = errorCode === 'Ai.SpeechToText.NotRecognized';

          // One failure, one report: the caller's onError already surfaces every dictation
          // error, so raising a second notification here showed the user two for one failure.
          onError(isNotRecognized ? 'not-recognized' : 'api-error');
          setState('error');
        } finally {
          cleanup();
        }
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      startTimeRef.current = Date.now();
      pendingGenerationRef.current = transcriptionGenerationRef.current;
      setState('recording');
      setDuration(0);

      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);

      // Start silence detection
      const checkSilence = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
          detectSilence();
          requestAnimationFrame(checkSilence);
        }
      };
      requestAnimationFrame(checkSilence);
    } catch (err) {
      cleanup();
      // Check for permission denied - browsers throw NotAllowedError
      if (err instanceof Error && err.name === 'NotAllowedError') {
        onError('permission-denied');
      } else {
        onError('no-microphone');
      }
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }

  // Unmount only. `cleanup` stops the recording, so this effect must not re-run when its
  // identity changes — that would kill a recording in progress on every re-render. The
  // first instance is captured and never replaced: `cleanup` closes over refs alone, so
  // every instance does exactly the same thing.
  const cleanupRef = useRef(cleanup);

  useEffect(() => () => cleanupRef.current(), []);

  return {
    state,
    startRecording,
    stopRecording,
    duration,
    isSupported,
    isProcessing: state === 'processing',
    notifyManualEdit,
  };
}
